import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, ExternalLinkIcon, MailIcon } from 'lucide-react';
import { Button } from '@systemzero-inc/ui/primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { toast } from '~/hooks/use-toast';
import { buildGoogleCalendarUrl, buildOutlookCalendarUrl, downloadIcs, type CalendarEvent } from '~/lib/utils/calendar';
import { useCommandBarStore } from '~/stores/command-bar.store';

/** Returns now rounded up to the next 30-minute mark, formatted for the inputs. */
function defaultStart(): { date: string; time: string } {
  const now = new Date();
  const mins = now.getMinutes();
  now.setMinutes(mins < 30 ? 30 : 60, 0, 0);
  return {
    date: format(now, 'yyyy-MM-dd'),
    time: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
  };
}

/**
 * Combines a date string (`yyyy-MM-dd`) and a time string (`HH:mm`) into a
 * local Date object.
 */
function combine(dateStr: string, timeStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [h, min] = timeStr.split(':').map(Number);
  return new Date(y, (m ?? 1) - 1, d, h, min, 0, 0);
}

/**
 * Quick "create meeting" dialog. Lets the user enter a title, time, and
 * optional attendees, then either downloads an `.ics` file (universal —
 * opens in Mac Calendar / Outlook desktop / etc.) or jumps straight to
 * Google Calendar / Outlook Web with the event pre-filled.
 *
 * Triggered from the command bar's "Add meeting to calendar" action.
 */
export function MeetingDialog() {
  const open = useCommandBarStore((s) => s.meetingDialogOpen);
  const close = useCommandBarStore((s) => s.closeMeetingDialog);

  const initialStart = useMemo(defaultStart, [open]);

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(initialStart.date);
  const [time, setTime] = useState(initialStart.time);
  const [duration, setDuration] = useState(30);
  const [attendees, setAttendees] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');

  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => titleInputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open]);

  const buildEvent = (): CalendarEvent => {
    const start = combine(date, time);
    const end = new Date(start.getTime() + duration * 60 * 1000);
    return {
      title: title.trim() || 'Meeting',
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      start,
      end,
      attendees: attendees
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s.includes('@')),
    };
  };

  const handleDownloadIcs = () => {
    const event = buildEvent();
    downloadIcs(event, `${event.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'meeting'}.ics`);
    toast({
      title: 'Meeting file downloaded',
      description: 'Open the .ics file to add it to your calendar.',
      variant: 'success',
    });
    close();
  };

  const handleOpenGoogle = () => {
    window.open(buildGoogleCalendarUrl(buildEvent()), '_blank', 'noopener,noreferrer');
    close();
  };

  const handleOpenOutlook = () => {
    window.open(buildOutlookCalendarUrl(buildEvent()), '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? close() : null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add meeting to calendar</DialogTitle>
          <DialogDescription>
            Generate a meeting invite. Works with Mac Calendar, Outlook, and Google Calendar.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="meeting-title">Title</Label>
            <Input
              id="meeting-title"
              ref={titleInputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Quarterly business review"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="meeting-date">Date</Label>
              <Input id="meeting-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="meeting-time">Time</Label>
              <Input id="meeting-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="meeting-duration">Duration (min)</Label>
              <Input
                id="meeting-duration"
                type="number"
                min={5}
                step={5}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value) || 30)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="meeting-attendees">Attendees (comma-separated emails)</Label>
            <Input
              id="meeting-attendees"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="alice@example.com, bob@example.com"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="meeting-location">Location</Label>
            <Input
              id="meeting-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Zoom link, room, or address"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="meeting-description">Description</Label>
            <Textarea
              id="meeting-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Agenda, notes, or context"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button type="button" variant="default" onClick={handleDownloadIcs} className="gap-2">
            <CalendarIcon className="size-4" />
            Download .ics
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleOpenGoogle} className="gap-2">
              <ExternalLinkIcon className="size-4" />
              Google Calendar
            </Button>
            <Button type="button" variant="outline" onClick={handleOpenOutlook} className="gap-2">
              <MailIcon className="size-4" />
              Outlook Web
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
